import { useEffect, useState } from 'react';
import { Check, Eye, X } from '@phosphor-icons/react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

export type GraphieStatus = 'pending' | 'validated' | 'edited' | 'rejected';

export interface GraphieAdviceView {
  id: string;
  defautId: string;
  lettre: string | null;
  intitule: string;
  occurrences: readonly string[];
  confidence: 'haute' | 'moyenne' | 'faible';
  proposedConseil: string;
  conseil: string;
  status: GraphieStatus;
}

export interface GraphieAnalysisView {
  texteId: string;
  pointPositif: string;
  advices: readonly GraphieAdviceView[];
  gaps: ReadonlyArray<{ attendu: string; recopie: string; nature: 'oubli' | 'substitution' | 'ajout' }>;
  teacherNote: string;
  newPhotoRequired: boolean;
  published_to_student: boolean;
  purge_after: string;
}

export interface GraphieTexteView {
  id: string;
  niveau: 'college' | 'lycee';
  titre: string;
  texte: string;
}

export interface GraphieReviewDraft {
  pointPositif: string;
  advices: Array<{ id: string; status: GraphieStatus; conseil: string }>;
}

export interface GraphiePanelProps {
  listTextes: () => Promise<GraphieTexteView[]>;
  load: () => Promise<GraphieAnalysisView | null>;
  run: (texteId: string) => Promise<GraphieAnalysisView>;
  review: (draft: GraphieReviewDraft) => Promise<GraphieAnalysisView>;
  publish: (published: boolean) => Promise<void>;
  defaultOpen?: boolean;
  fixedTexteId?: string | null;
}

const STATUS_LABEL: Record<GraphieStatus, string> = {
  pending: 'À trancher',
  validated: 'Validé',
  edited: 'Modifié',
  rejected: 'Écarté',
};

const NIVEAU_LABEL = { college: 'Collège', lycee: 'Lycée' } as const;

function toDraft(a: GraphieAnalysisView): GraphieReviewDraft {
  return { pointPositif: a.pointPositif, advices: a.advices.map((x) => ({ id: x.id, status: x.status, conseil: x.conseil })) };
}

function serialize(d: GraphieReviewDraft | null): string {
  return d ? JSON.stringify(d) : '';
}

function retained(s: GraphieStatus): boolean {
  return s === 'validated' || s === 'edited';
}

export default function GraphiePanel({ listTextes, load, run, review, publish, defaultOpen = false, fixedTexteId = null }: GraphiePanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [baseline, setBaseline] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [textes, setTextes] = useState<GraphieTexteView[]>([]);
  const [texteId, setTexteId] = useState('');
  const [analysis, setAnalysis] = useState<GraphieAnalysisView | null>(null);
  const [draft, setDraft] = useState<GraphieReviewDraft | null>(null);
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = (a: GraphieAnalysisView | null) => {
    setAnalysis(a);
    setDraft(a ? toDraft(a) : null);
    setBaseline(serialize(a ? toDraft(a) : null));
    setPublished(a?.published_to_student ?? false);
    if (a) setTexteId(a.texteId);
    else if (fixedTexteId) setTexteId(fixedTexteId);
  };

  useEffect(() => {
    if (!open || textes.length > 0) return;
    Promise.all([listTextes(), load()])
      .then(([t, a]) => {
        setTextes(t);
        setTexteId((cur) => fixedTexteId || cur || t[0]?.id || '');
        apply(a);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open]);

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    setJustSaved(false);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const analyse = () =>
    guard(async () => {
      apply(await run(texteId));
    });

  const setAdvice = (id: string, patch: Partial<{ status: GraphieStatus; conseil: string }>) =>
    setDraft((d) => (d ? { ...d, advices: d.advices.map((a) => (a.id === id ? { ...a, ...patch } : a)) } : d));

  const decide = (a: GraphieAdviceView, status: GraphieStatus) => {
    const cur = draft?.advices.find((x) => x.id === a.id);
    setAdvice(a.id, { status: status === 'validated' && cur && cur.conseil.trim() !== a.proposedConseil ? 'edited' : status });
  };

  const save = () =>
    guard(async () => {
      if (!draft) return;
      apply(await review(draft));
      setNotice('Revue enregistrée.');
      setJustSaved(true);
    });

  const pendingCount = draft ? draft.advices.filter((a) => a.status === 'pending').length : 0;
  const retainedCount = draft ? draft.advices.filter((a) => retained(a.status)).length : 0;
  const nothingToPublish = retainedCount === 0 && (draft?.pointPositif.trim() ?? '') === '';
  const publishBlocked = !published && (pendingCount > 0 || nothingToPublish || Boolean(analysis?.newPhotoRequired));
  const dirty = draft !== null && serialize(draft) !== baseline;
  const statusLabel = published ? 'Publiée' : analysis ? 'À valider' : 'À analyser';

  const togglePublish = () =>
    guard(async () => {
      if (!draft) return;
      if (published) {
        await publish(false);
        setPublished(false);
        return;
      }
      apply(await review(draft));
      await publish(true);
      setPublished(true);
      setNotice('Conseils publiés à l’élève.');
    });

  const texte = textes.find((t) => t.id === texteId);

  return (
    <div className="w-full mt-3 rounded-card border border-border" data-testid="graphie-panel">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 min-h-[44px] px-3 border-none bg-transparent text-left text-[13px] font-bold text-foreground cursor-pointer"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex flex-wrap items-center gap-2">
          Recopie de référence — analyse de la graphie{defaultOpen ? '' : ' (facultatif)'}
          <Badge variant={published ? 'default' : 'secondary'} data-testid="graphie-status">{statusLabel}</Badge>
        </span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="flex flex-col gap-3 px-3 pb-3">
          <p className="m-0 text-[12px] text-muted-foreground">
            Analyse complémentaire, sans note : elle repère les lettres ambiguës à partir de la photo. Rien n’est visible de l’élève avant votre validation.
          </p>
          {error && <Alert>{error}</Alert>}
          {notice && (
            <div role="status" className="flex items-center gap-2 px-3 py-2 rounded-card bg-[var(--naia-green-bg)] text-[var(--naia-green-deep)] text-[13px] font-semibold">
              <Check size={15} weight="bold" />
              {notice}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-semibold text-foreground" htmlFor="graphie-texte">
              Texte à recopier
            </label>
            <NativeSelect id="graphie-texte" value={texteId} onChange={(e) => setTexteId(e.target.value)} disabled={busy || Boolean(fixedTexteId)}>
              {textes.map((t) => (
                <option key={t.id} value={t.id}>
                  {NIVEAU_LABEL[t.niveau]} — {t.titre}
                </option>
              ))}
            </NativeSelect>
            {fixedTexteId && <p className="m-0 text-[12px] text-muted-foreground">Texte fixé pour ce devoir.</p>}
            {texte && <p className="m-0 p-2 rounded-card bg-muted text-[12.5px] leading-relaxed text-foreground">{texte.texte}</p>}
            <div>
              <Button
                variant={analysis ? 'secondary' : 'default'}
                className="min-h-[44px]"
                disabled={busy || !texteId || published}
                onClick={() => void analyse()}
              >
                <Eye size={15} weight="bold" className="mr-1.5" />
                {busy && !analysis ? 'Analyse en cours…' : analysis ? 'Relancer l’analyse' : 'Analyser la graphie'}
              </Button>
              {published && <p className="mt-1 mb-0 text-[12px] text-muted-foreground">Dépubliez avant de relancer l’analyse.</p>}
            </div>
          </div>

          {analysis && draft && (
            <>
              {analysis.newPhotoRequired && (
                <Alert>Photo insuffisante pour analyser la graphie : demandez une nouvelle photo à l’élève.</Alert>
              )}
              {analysis.teacherNote && (
                <p className="m-0 p-2 rounded-card bg-[var(--naia-info-bg)] text-[12.5px] text-foreground" data-testid="graphie-teacher-note">
                  <strong>Note pour vous :</strong> {analysis.teacherNote}
                </p>
              )}
              {analysis.gaps.length > 0 && (
                <div className="text-[12.5px] text-foreground">
                  <div className="font-bold mb-1">Écarts avec le texte de référence (copie, hors graphie — non montrés à l’élève)</div>
                  <ul className="m-0 pl-5">
                    {analysis.gaps.map((g, i) => (
                      <li key={i}>
                        {g.nature === 'oubli' ? 'Oubli' : g.nature === 'ajout' ? 'Ajout' : 'Substitution'} : attendu « {g.attendu} »
                        {g.recopie ? `, lu « ${g.recopie} »` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <label className="flex flex-col gap-1 text-[13px] font-semibold text-foreground">
                Point positif (montré à l’élève)
                <Textarea
                  rows={2}
                  value={draft.pointPositif}
                  disabled={busy}
                  onChange={(e) => setDraft((d) => (d ? { ...d, pointPositif: e.target.value } : d))}
                />
              </label>

              {analysis.advices.length === 0 && !analysis.newPhotoRequired && (
                <p className="m-0 text-[13px] text-muted-foreground">Aucun défaut de graphie repéré avec assez de certitude : vous pouvez publier le point positif seul.</p>
              )}

              {analysis.advices.map((a) => {
                const cur = draft.advices.find((x) => x.id === a.id)!;
                return (
                  <div key={a.id} className="flex flex-col gap-2 rounded-card border border-border p-3" data-testid={`advice-${a.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {a.lettre && <Badge variant="outline">{a.lettre}</Badge>}
                      <span className="text-[13.5px] font-bold text-foreground">{a.intitule}</span>
                      <Badge variant={retained(cur.status) ? 'default' : 'secondary'}>{STATUS_LABEL[cur.status]}</Badge>
                    </div>
                    <p className="m-0 text-[12px] text-muted-foreground">
                      Repéré dans : {a.occurrences.join(', ')} — confiance {a.confidence} ({a.defautId})
                    </p>
                    <Textarea
                      rows={2}
                      aria-label={`Conseil — ${a.intitule}`}
                      value={cur.conseil}
                      disabled={busy}
                      onChange={(e) => setAdvice(a.id, { conseil: e.target.value, status: retained(cur.status) ? 'edited' : cur.status })}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" className="min-h-[44px]" disabled={busy} onClick={() => decide(a, 'validated')}>
                        <Check size={14} weight="bold" className="mr-1" />
                        Valider
                      </Button>
                      <Button size="sm" variant="secondary" className="min-h-[44px]" disabled={busy} onClick={() => decide(a, 'rejected')}>
                        <X size={14} weight="bold" className="mr-1" />
                        Écarter
                      </Button>
                    </div>
                  </div>
                );
              })}

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" className="min-h-[44px]" disabled={busy || !dirty} onClick={() => void save()}>
                  {!dirty && justSaved ? '✓ Enregistré' : 'Enregistrer'}
                </Button>
                <Button
                  variant={published ? 'default' : 'secondary'}
                  className="min-h-[44px]"
                  disabled={busy || publishBlocked}
                  onClick={() => void togglePublish()}
                >
                  {published ? '✓ Publié à l’élève — dépublier' : 'Publier à l’élève'}
                </Button>
              </div>
              {publishBlocked && !published && (
                <p className="m-0 text-[12px] text-muted-foreground">
                  {pendingCount > 0
                    ? `${pendingCount} conseil${pendingCount > 1 ? 's' : ''} à valider, modifier ou écarter avant de publier.`
                    : analysis?.newPhotoRequired
                      ? 'Photo inexploitable : demandez une nouvelle photo avant de publier.'
                      : 'Rédigez un point positif ou retenez un conseil pour publier.'}
                </p>
              )}
              <p className="m-0 text-[12px] text-muted-foreground">
                Conservation : l’analyse est supprimée le {new Date(analysis.purge_after).toLocaleDateString('fr-FR')}.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
